import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DragulaService } from 'ng2-dragula';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { CustomTrackerId } from '../../../../data/dietitian/domain/custom_tracker/custom_tracker';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../data/patient/domain/patient';
import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';
import {
  TrackerBatchSaved,
  TrackerCreated,
  TrackerDeleted,
  TrackerUpdated,
} from '../../../../data/tracker/domain/events/tracker/tracker_events';
import {
  Tracker,
  TrackerProps,
} from '../../../../data/tracker/domain/tracker/tracker';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { NewTrackerModalComponent } from '../new-tracker-modal/new-tracker-modal.component';
import { DiaryTypeEditPopupComponent } from './component/diary-type-edit-popup/diary-type-edit-popup.component';
import {
  TrackerCellModel,
  TrackerCellModelsWrapper,
  TrackerManagerService,
} from './tracker-manager.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-tracker-manager',
  templateUrl: './tracker-manager.component.html',
  styleUrls: ['./tracker-manager.component.scss'],
  providers: [TrackerManagerService],
})
export class TrackerManagerComponent implements OnInit, OnDestroy {
  @Input() public dietitian: Dietitian | undefined;
  @Input() public patientGroup: PatientGroup | undefined;
  @Input() public patient: Patient | undefined;
  @Input() public maxTrackers = 10;
  @Input() public liveSave = true;
  @Input() public mode: 'PATIENT' | 'PATIENT_GROUP' | 'DIETITIAN' | 'UNKNOWN' =
    'UNKNOWN';
  @Input() public withNewTrackerButton = true;

  public events: DiaryType[];
  public trackers: TrackerCellModelsWrapper;
  public currentTrackers: TrackerCellModel[];
  private dragEndSubscription: Subscription;

  public set selectedTrackerType(value: DiaryType) {
    this._currentDiaryType = value;
    this.loadSelectedTrackers();
  }

  public get selectedTrackerType(): DiaryType {
    return this._currentDiaryType;
  }

  public activeTrackers?: { [key: string]: number };
  public loading = true;
  public error = false;
  private bsModalRef: BsModalRef;
  private navigationSub: Subscription;
  private trackerEventSubscription: Subscription;
  private _currentDiaryType: DiaryType;

  constructor(
    private trackerManagerService: TrackerManagerService,
    private activatedRoute: ActivatedRoute,
    private modalService: BsModalService,
    private toastrService: ToastrService,
    private dragulaService: DragulaService,
  ) {
    this.dragulaService.createGroup('DRAG_TRACKERS', {
      copy: () => {
        return false;
      },
      moves: () => {
        return true;
      },
      accepts: (el, target, source): boolean => {
        if (!el || !target || !source) {
          return false;
        }
        if (target.classList.contains('disabled')) {
          return false;
        }
        const targetId = target.getAttribute('id');
        const sourceId = source.getAttribute('id');
        if (targetId === 'trackers-list') {
          return true;
        }
        return sourceId === 'trackers-list' && target === source;
      },
    });

    this.dragEndSubscription = this.dragulaService
      .dragend('DRAG_TRACKERS')
      .subscribe(() => {
        let index = 0;
        this.currentTrackers.forEach((value) => {
          value.entity.sort = index++;
        });
        this.trackerManagerService
          .saveTrackers(this.currentTrackers.map((t) => t.entity))
          .then();
      });
  }

  get isNewPatientGroup(): boolean {
    return !this.patientGroup || this.patientGroup.id.toString() === '_NEW_';
  }

  ngOnInit(): void {
    this.trackerManagerService
      .getTrackers(
        this.dietitian ? this.dietitian.id.toString() : undefined,
        !this.isNewPatientGroup ? this.patientGroup?.id.toString() : undefined,
        this.patient ? this.patient.id.toString() : undefined,
      )
      .then((trackers) => {
        if (trackers.trackerCellModels.length > 0) {
          this.trackers = trackers;
          this.trackerManagerService
            .countActiveTrackers(this.trackers)
            .then((result) => (this.activeTrackers = result));
          if (
            this.mode === 'PATIENT' &&
            this.trackers.origin !== 'PATIENT' &&
            this.patient
          ) {
            this.trackerManagerService
              .saveTrackersPatient(this.trackers, this.patient)
              .then();
          }
          this.loadEvents().then();
          this.loading = false;
        }
      });

    this.navigationSub = this.activatedRoute.queryParams.subscribe((params) => {
      if (this.events) {
        this.selectedTrackerType =
          params['type'] ?? this.sortEvents(this.events)[0];
      }
    });

    this.trackerEventSubscription =
      this.trackerManagerService.trackerEvents.subscribe((event) => {
        if (event instanceof TrackerCreated) {
          this.trackers.trackerCellModels.push(
            TrackerCellModel.fromTracker(
              Tracker.create(
                {
                  dietitianTrackerId: CustomTrackerId.create(event.entity.id),
                  activatedAt: event.entity.activatedAt,
                  name: event.entity.name,
                  type: event.entity.type,
                  diaryType: event.entity.diaryType,
                  min: event.entity.min,
                  max: event.entity.max,
                  steps: event.entity.steps,
                } as TrackerProps,
                event.entity.id,
              ),
            ),
          );
        } else if (event instanceof TrackerBatchSaved) {
          this.trackerManagerService
            .getTrackers(
              this.dietitian ? this.dietitian.id.toString() : undefined,
              !this.isNewPatientGroup
                ? this.patientGroup?.id.toString()
                : undefined,
              this.patient ? this.patient.id.toString() : undefined,
            )
            .then((t) => (this.trackers = t));
        } else if (event instanceof TrackerUpdated) {
          this.trackerManagerService
            .getTrackers(
              this.dietitian ? this.dietitian.id.toString() : undefined,
              !this.isNewPatientGroup
                ? this.patientGroup?.id.toString()
                : undefined,
              this.patient ? this.patient.id.toString() : undefined,
            )
            .then((t) => (this.trackers = t));
        } else if (event instanceof TrackerDeleted) {
          this.trackerManagerService
            .getTrackers(
              this.dietitian ? this.dietitian.id.toString() : undefined,
              !this.isNewPatientGroup
                ? this.patientGroup?.id.toString()
                : undefined,
              this.patient ? this.patient.id.toString() : undefined,
            )
            .then((t) => (this.trackers = t));
        }
      });
  }

  ngOnDestroy(): void {
    this.navigationSub?.unsubscribe();
    this.trackerEventSubscription?.unsubscribe();
    this.dragEndSubscription?.unsubscribe();
    this.dragulaService.destroy('DRAG_TRACKERS');
  }

  get isAllowedToActivate() {
    return (
      this.trackers?.trackerCellModels
        .filter((t) => t.entity.diaryType === this.selectedTrackerType.name)
        .filter((tracker) => tracker.checked).length < this.maxTrackers
    );
  }

  changeTrackerType(type: DiaryType) {
    this.selectedTrackerType = type;
  }

  async onTrackerToggle(tracker: Tracker, checked: boolean) {
    if (this.liveSave) {
      tracker.activatedAt = checked ? new Date() : undefined;
      this.trackerManagerService
        .toggleTracker(tracker.props, tracker.trackerId?.id?.toString())
        .then();
      return tracker;
    } else {
      tracker.activatedAt = checked ? new Date() : undefined;
      return tracker;
    }
  }

  onEditTracker(tracker: Tracker) {
    const initialState: ModalOptions = {
      initialState: {
        type: this.selectedTrackerType,
        tracker: tracker,
        diaryTypes: this.events,
      },
      class: 'modal-lg modal-dialog-centered',
    };
    this.bsModalRef = this.modalService.show(
      NewTrackerModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof NewTrackerModalComponent) {
      this.bsModalRef.content.validate.subscribe((trackerProps) => {
        this.trackerManagerService
          .saveTracker(trackerProps, tracker.id.toString())
          .then(() => {
            const expected = this.trackers.trackerCellModels.filter(
              (tcm) => tcm.entity.id.toString() === tracker.id.toString(),
            );
            if (expected && expected.length === 1) {
              expected[0].entity = Tracker.create(trackerProps, tracker.id);
            }
          })
          .catch((e: any) =>
            console.log('🔴 trackerManagerService.saveTracker', e),
          );
      });
    }
  }

  onDeleteTracker(tracker: Tracker) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: `Supprimer ce tracker ?`,
        subtitle:
          'Voulez-vous vraiment supprimer ce tracker ? Cette action est irréversible.',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(async () => {
        this.deleteTracker(tracker.id.toString()).then(() => {
          let index: number | undefined;
          for (let i = 0; i < this.trackers.trackerCellModels.length; i++) {
            if (
              this.trackers.trackerCellModels[i].entity.id.toString() ===
              tracker.id.toString()
            ) {
              index = i;
              break;
            }
          }
          if (index) {
            this.trackers.trackerCellModels.splice(index, 1);
            this.loadSelectedTrackers();
          }
        });
      });
    }
  }

  async deleteTracker(trackerId: string) {
    return this.trackerManagerService.removeTracker(
      this.dietitian?.dietitianId.id.toString() ?? undefined,
      !this.isNewPatientGroup ? this.patientGroup?.id.toString() : undefined,
      this.patient?.id.toString() ?? undefined,
      trackerId,
    );
  }

  onNewTracker() {
    const initialState: ModalOptions = {
      initialState: {
        type: this.selectedTrackerType,
        diaryTypes: this.events,
      },
      class: 'modal-lg modal-dialog-centered',
    };
    this.bsModalRef = this.modalService.show(
      NewTrackerModalComponent,
      initialState,
    );

    if (this.bsModalRef.content instanceof NewTrackerModalComponent) {
      this.bsModalRef.content.validate.subscribe((tracker) => {
        tracker.dietitianId = this.dietitian?.dietitianId;
        tracker.patientGroupId = !this.isNewPatientGroup
          ? this.patientGroup?.patientGroupId
          : undefined;
        tracker.patientId = this.patient?.patientId;
        tracker.activatedAt = new Date();
        this.trackerManagerService
          .saveTracker(tracker)
          .then(() => this.loadSelectedTrackers());
      });
    }
  }

  get type() {
    return this.events.filter((e) => e.name === 'collation')[0];
  }

  getOrigin(origin: 'PATIENT' | 'PATIENT_GROUP' | 'DIETITIAN' | 'UNKNOWN') {
    switch (origin) {
      case 'PATIENT':
        return 'Configuration patient';
      case 'PATIENT_GROUP':
        return 'Configuration de groupe';
      case 'DIETITIAN':
        return 'Configuration par défaut';
      case 'UNKNOWN':
        return '';
    }
  }

  selectedTrackers(
    trackerCellModels: TrackerCellModel[],
    selectedTrackerType: DiaryType,
  ) {
    return trackerCellModels.filter(
      (t) => t.entity.diaryType === selectedTrackerType.name,
    );
  }

  async saveAllTrackers(group: PatientGroup | undefined) {
    if (this.mode === 'PATIENT_GROUP' && group) {
      this.trackers = await this.trackerManagerService.saveTrackersGroup(
        this.events,
        this.trackers,
        group,
      );
    }
  }

  async editDiaryType(diaryType: DiaryType) {
    const modal = this.modalService.show(DiaryTypeEditPopupComponent, {
      backdrop: false,
      class: 'modal-dialog-centered modal-shadow',
      initialState: { diaryType },
    });
    if (modal.content instanceof DiaryTypeEditPopupComponent) {
      modal.content.action.subscribe(async (response) => {
        if (response) {
          diaryType.label = response.label;
          diaryType.hint = response.hint;
          if (this.isNewPatientGroup) {
            await this.trackerManagerService.setEvent(
              response,
              this.dietitian?.id.toString(),
              this.patientGroup?.id.toString(),
              this.patient?.id.toString(),
            );
            this.toastrService.success("Type d'événement sauvegardé.");
          }
        } else {
          this.toastrService.error(
            "Une erreur s'est produite, veuillez essayer ultérieurement.",
          );
        }
      });
    }
  }

  async maskDiaryType(diaryType: DiaryType) {
    diaryType.displayed = !diaryType.displayed;
    if (this.isNewPatientGroup) {
      await this.trackerManagerService.setEvent(
        diaryType,
        this.dietitian?.id.toString(),
        this.patientGroup?.id.toString(),
        this.patient?.id.toString(),
      );
    }
  }

  async loadEvents() {
    try {
      const events = await this.trackerManagerService.getEvents(
        this.dietitian ? this.dietitian.id.toString() : undefined,
        !this.isNewPatientGroup ? this.patientGroup?.id.toString() : undefined,
        this.patient ? this.patient.id.toString() : undefined,
      );
      this.events = events;
      this.selectedTrackerType = this.sortEvents(this.events)[0];
    } catch (e) {
      console.error('loadEvents', e);
    }
  }

  sortEvents(events: DiaryType[]) {
    return events.sort((a, b) => {
      if (a.displayed && !b.displayed) {
        return -1;
      } else if (!a.displayed && b.displayed) {
        return 1;
      } else {
        if (a.sorter < b.sorter) {
          return -1;
        } else if (a.sorter > b.sorter) {
          return 1;
        } else {
          return 0;
        }
      }
    });
  }

  private loadSelectedTrackers() {
    this.currentTrackers = [];
    const selectedTrackers = this.selectedTrackers(
      this.trackers.trackerCellModels,
      this._currentDiaryType,
    ).sort((a, b) => {
      if (a.entity.sort < b.entity.sort) {
        return -1;
      } else if (a.entity.sort > b.entity.sort) {
        return 1;
      } else {
        return 0;
      }
    });
    for (const item of selectedTrackers) {
      this.currentTrackers.push(item);
    }
  }
}
